import * as R from "ramda";
import { isEmptyOrNil, noop } from "utils/General";
import { call, cancel, delay, fork, take } from "redux-saga/effects";
import {
  MAX_SIZE_2GB,
  TRANSFORMATION_CONFIG,
  TRANSFORMATION_CROP_RECTANGLE_CONFIG,
  TRANSFORMATION_CROP_SQUARE_CONFIG
} from "./constants";

export const makeFuture = () => {
  let done, error;
  const prom = new Promise((res, rej) => {
    done = res;
    error = rej;
  });

  return {
    done,
    error,
    onRealized: R.always(prom)
  };
};

export const debounce = (pattern, delegate, time = 250) => {
  const delayAndCall = function*(...args) {
    yield delay(time);
    yield call(delegate, ...args);
  };

  return function*() {
    let task;
    while (true) {
      const action = yield take(pattern);
      if (task) {
        yield cancel(task);
      }
      task = yield fork(delayAndCall, action);
    }
  };
};

/**
 * Get the element that initially shows as a checkbox by the data-e2e value (this comes from
 * filestack) and modify the styles and content
 * @param dataE2eValue
 */
const updateButtonSpan = (dataE2eValue = "") => {
  if (!dataE2eValue.value) return;
  const button = document.querySelector(`button[data-e2e="${dataE2eValue}"]`);
  if (button) {
    button.style.opacity = 1;
    const span = button.querySelector("span");
    if (span) {
      span.classList.remove("fs-icon");
      span.textContent = `Apply ${dataE2eValue.split("-")[0]}`;
      span.style.fontFamily = "proxima-nova, arial, sans-serif";
      span.style.padding = "7.5px 30px";
      span.style.fontSize = "10px";
    }
  }
};

/**
 * Invoke a function to modify the styles of the resize and crop buttons
 */
const modifyTransformUICheckboxButtons = () => {
  updateButtonSpan("resize-done");
  updateButtonSpan("crop-done");
};

/**
 * This helps to detect if the "Transform" option in the Transformation UI was clicked
 * @param event click event
 */
const renameButtonsOnTransformOption = event => {
  const target = event.target.closest('a[data-e2e="transforms"]');
  if (target) {
    modifyTransformUICheckboxButtons();
  }
};

const hideBottomToolbar = () =>
  document
    .querySelectorAll(".fs-nav, .fs-footer, .fs-splitted")
    .forEach(element => {
      element.style.display = "none";
    });

const showBottomToolbar = () =>
  document
    .querySelectorAll(".fs-nav, .fs-footer, .fs-splitted")
    .forEach(element => {
      element.style.display = "inherit";
    });

const hideSaveButton = () => {
  const saveButton = document.querySelector('button[data-e2e="Save"]');
  if (saveButton) {
    saveButton.style.display = "none";
  }
};

const showSaveButton = () => {
  const saveButton = document.querySelector('button[data-e2e="Save"]');
  if (saveButton) {
    saveButton.style.display = "inherit";
  }
};

const hideFlipAndRotateBar = () => {
  const transformTopBar = document.querySelector(".fs-transform-top");
  if (transformTopBar) {
    transformTopBar.style.display = "none";
  }
};

const hideResizeOptions = () => {
  const resizeContainer = document.querySelector(".fs-resize");
  if (resizeContainer) {
    resizeContainer.style.display = "none";
  }
};

const hideNotAllowedRatios = (enforceCroppingToContinue = true) => {
  const ulElement = document.querySelector(".fs-ratio-list");
  if (ulElement) {
    const firstChild = ulElement.firstElementChild;
    while (ulElement.lastElementChild !== firstChild) {
      ulElement.removeChild(ulElement.lastElementChild);
    }
    if (firstChild && enforceCroppingToContinue) {
      firstChild.firstElementChild.click();
    }
  }
  setTimeout(() => {
    modifyTransformUICheckboxButtons();
    if (enforceCroppingToContinue) {
      hideSaveButton();
      hideBottomToolbar();
    }
  }, 1000);
};

const clickOnTransformButton = () => {
  const transformButton = document.querySelector('a[data-e2e="transforms"]');
  if (transformButton) {
    transformButton.click();
    setTimeout(() => {
      hideNotAllowedRatios();
      hideResizeOptions();
    }, 2000);
  }
};

const enforceCroppingOnTransformOption = event => {
  const target = event.target.closest('a[data-e2e="transforms"]');
  if (target) {
    setTimeout(() => {
      hideResizeOptions();
      hideFlipAndRotateBar();
      hideNotAllowedRatios();
    }, 2000);
  }
};

const exitCropScreen = () => {
  const backButton = document.querySelector(".fs-back");
  if (backButton) {
    backButton.firstElementChild.click();
  }
};

const showHiddenOptionsAfterCropping = event => {
  const target = event.target.closest('button[data-e2e="crop-done"]');
  if (target) {
    showSaveButton();
    showBottomToolbar();
    exitCropScreen();
  }
};

const getAcceptConfig = accept => {
  if (isEmptyOrNil(accept) || accept === "all") {
    return {};
  }
  if (Array.isArray(accept)) {
    const filteredAccept = accept.filter(format => !isEmptyOrNil(format));
    if (filteredAccept.length === 0) {
      return {};
    }
    return { accept: filteredAccept };
  }
  return { accept };
};

export const openTransformationUI = async ({
  filesUploaded,
  response,
  filePickerKey,
  filePickerInstance,
  onDone = undefined,
  forceSquareCrop = false,
  forceRectangleCrop = false
}) => {
  if (!filesUploaded) return;
  const imageTransformUI = new window.FilestackTransform(filePickerKey);
  let config = TRANSFORMATION_CONFIG;
  if (forceSquareCrop) {
    config = TRANSFORMATION_CROP_SQUARE_CONFIG;
  } else if (forceRectangleCrop) {
    config = TRANSFORMATION_CROP_RECTANGLE_CONFIG;
  }
  imageTransformUI.setConfig(config);
  /**
   * When the user clicks on "Transform" option, update apply buttons for better looking
   */
  document.addEventListener("click", renameButtonsOnTransformOption);
  const transformedFiles = await Promise.all(
    filesUploaded.map(async file => {
      if (!file?.mimetype?.includes("image")) {
        return file;
      } else {
        /**
         * For Speaker Headshot & Sponsor Logo, redirect user to Transform module immediately
         */
        if (forceSquareCrop || forceRectangleCrop) {
          document.addEventListener("click", enforceCroppingOnTransformOption);
          document.addEventListener("click", showHiddenOptionsAfterCropping);
          setTimeout(() => {
            clickOnTransformButton();
          }, 1000);
        }
        const transformedFilePromise = await imageTransformUI.open(file.url);
        const transformedFileContent = await fetch(transformedFilePromise);
        const blobContent = await transformedFileContent.blob();
        const originalFileName = file?.filename || "default-file.png";
        const fileExtension = originalFileName.split(".").pop();
        const fileName = `${originalFileName.replace(
          `.${fileExtension}`,
          ""
        )}-transformed.${fileExtension}`;
        const fileObject = new File([blobContent], fileName, {
          type: blobContent.type
        });
        const base64Image = await new Promise((resolve, reject) => {
          const fileReader = new FileReader();
          fileReader.onload = () => resolve(fileReader.result);
          fileReader.onerror = reject;
          fileReader.readAsDataURL(fileObject);
        });
        const inputFile = `data:${blobContent.type};base64,${
          base64Image.split(",")[1]
        }`;
        // Remove listeners before moving on
        document.removeEventListener("click", enforceCroppingOnTransformOption);
        document.removeEventListener("click", showHiddenOptionsAfterCropping);
        return await filePickerInstance.upload(
          inputFile,
          {},
          { filename: fileName }
        );
      }
    })
  );
  document.removeEventListener("click", renameButtonsOnTransformOption);

  if (onDone) {
    const files = transformedFiles.map(transformed => ({
      ...transformed,
      // eslint-disable-next-line no-underscore-dangle
      ...transformed?._file,
      filename: transformed?.name || transformed?.originalFile?.name,
      // eslint-disable-next-line no-underscore-dangle
      mimetype: transformed?._file?.type || transformed?.originalFile?.type
    }));
    onDone({ filesUploaded: files });
  } else {
    response(transformedFiles);
  }
};

export const openFilePicker = ({
  multiple = false,
  displayMode = "overlay",
  fromSources = ["local_file_system", "dropbox"],
  maxSize = 2147483648, // 2gb
  storeTo = {},
  accept,
  onFileUploadFailed = noop
}) =>
  new Promise(response => {
    // eslint-disable-next-line no-underscore-dangle
    const filePickerKey = window.__FILEPICKER_API_KEY__;
    const filePickerInstance = window.filestack.init(filePickerKey);
    if (filePickerInstance) {
      const acceptConfig = getAcceptConfig(accept);
      filePickerInstance
        .picker(
          R.filter(R.compose(R.not, R.isNil), {
            maxFiles: multiple ? 50 : 1,
            displayMode,
            fromSources,
            maxSize,
            storeTo: {
              location: "s3",
              path: "event-files/",
              ...storeTo
            },
            onUploadDone: ({ filesUploaded }) =>
              openTransformationUI({
                filesUploaded,
                response,
                filePickerKey,
                filePickerInstance
              }),
            onFileUploadFailed: error => onFileUploadFailed(error),
            transformations: {},
            ...acceptConfig
          })
        )
        .open();
    } else if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("Filestack not initialized");
    }
  });

export const renderDropZone = ({
  multiple = false,
  displayMode = "dropPane",
  container = "dropzone-container",
  fromSources = ["local_file_system", "dropbox"],
  maxSize = MAX_SIZE_2GB,
  storeTo = {},
  transformations = {},
  accept = [],
  maxFiles,
  onFileUploadProgress = null,
  onFileUploadCancel = null,
  onFileUploadFailed = null,
  onFileUploadFinished = null,
  dropPane = {},
  uploadConfig = {},
  customText = "Drag and drop a file here or click to choose one from your computer. We accept files up to 2GB.",
  onFileSelected = noop
}) =>
  new Promise((res, rej) => {
    // eslint-disable-next-line no-underscore-dangle
    const filepicker = window.filestack.init(window.__FILEPICKER_API_KEY__);
    if (filepicker) {
      const acceptConfig = getAcceptConfig(accept);
      const picker = filepicker.picker(
        R.filter(R.compose(R.not, R.isNil), {
          maxFiles: maxFiles ? maxFiles : multiple ? 50 : 1,
          displayMode,
          container,
          fromSources,
          maxSize,
          storeTo: {
            location: "s3",
            path: "event-files/",
            ...storeTo
          },
          uploadConfig,
          onUploadDone: ({ filesUploaded }) => res(filesUploaded),
          onFileUploadProgress,
          onFileUploadCancel,
          onFileUploadFinished,
          onFileSelected,
          onFileUploadFailed: props => {
            if (onFileUploadFailed) {
              onFileUploadFailed(props);
            }
            return rej(props);
          },
          dropPane: {
            overlay: false,
            customText,
            ...dropPane
          },
          ...(isEmptyOrNil(transformations) ? {} : { transformations }),
          ...acceptConfig
        })
      );
      picker.open();

      document.addEventListener("renderDropZone-cancel-upload", function() {
        picker.cancel();
      });
    } else if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("Filestack not initialized");
    }
  });

export const openFileTransform = function*(fileUrl, { save = true } = {}) {
  // eslint-disable-next-line no-underscore-dangle
  const filepicker = window.filestack.init(window.__FILEPICKER_API_KEY__);
  // eslint-disable-next-line no-underscore-dangle
  const tr = new window.FilestackTransform(window.__FILEPICKER_API_KEY__);
  tr.setConfig({
    lang: "en",
    editor: {
      transforms: {
        enabled: true,
        lockRotation: false,
        lockFlip: false,
        ratios: [
          {
            icon: "crop_free",
            default: true,
            name: "Custom",
            shape: "rect"
          },
          {
            icon: `<svg><circle cx="10" cy="10" r="8" stroke="white" stroke-width="2" fill="transparent"/></svg>`,
            name: "Circle",

            lockRatio: true // Controls whether the ratio is user-editable
          },
          {
            icon: "crop_2_3",
            name: "2/3", // The default translation for this ratio
            shape: "rect", // rect, circle
            ratio: 2 / 3, // The image aspect ratio (a floating point number) or
            lockRatio: true // Controls whether the ratio is user-editable
          },
          {
            icon: "crop_9_16",
            name: "9/16", // The default translation for this ratio
            shape: "rect", // rect, circle
            ratio: 9 / 16, // The image aspect ratio (a floating point number) or
            lockRatio: true // Controls whether the ratio is user-editable
          }
        ]
      },
      text: {
        enabled: false
      },
      filters: {
        enabled: false
      },
      adjustments: {
        enabled: false
      }
    },
    output: {
      format: "png",
      quality: 1,
      blob: true,
      name: null
    }
  });
  const res = yield call(R.always(tr.open(fileUrl)));
  if (save) {
    return yield call(R.always(filepicker.upload(res)));
  }
  return res;
};
